import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material'
import { Warning, Logout as LogoutIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { useAuth } from '@/hooks/useAuth'

const SessionExpirationWarning: React.FC = () => {
  const { isAuthenticated, extendSession, logout, timeUntilExpiry } = useAuth()
  const [showWarning, setShowWarning] = useState(false)
  const [countdown, setCountdown] = useState(0)

  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false)
      return
    }

    const checkExpiration = () => {
      const timeLeft = timeUntilExpiry
      
      // Show warning when 5 minutes or less remain
      if (timeLeft > 0 && timeLeft <= 5 * 60 * 1000) {
        setShowWarning(true)
        setCountdown(Math.ceil(timeLeft / 1000))
      } else {
        setShowWarning(false)
      }
    }

    // Check immediately
    checkExpiration()

    // Check every 30 seconds
    const interval = setInterval(checkExpiration, 30 * 1000)

    return () => clearInterval(interval)
  }, [isAuthenticated, timeUntilExpiry])

  useEffect(() => {
    if (!showWarning) return

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Session expired, logout automatically
          logout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(countdownInterval)
  }, [showWarning, logout])

  const handleExtendSession = () => {
    extendSession()
    setShowWarning(false)
  }

  const handleLogout = () => {
    logout()
    setShowWarning(false)
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const progressValue = countdown > 0 ? ((5 * 60 - countdown) / (5 * 60)) * 100 : 100

  if (!showWarning) return null

  return (
    <Dialog
      open={showWarning}
      onClose={() => {}} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning />
        Session Expiring Soon
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Your session will expire in:
          </Typography>
          <Typography variant="h4" color="warning.main" align="center" sx={{ mb: 2 }}>
            {formatTime(countdown)}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progressValue} 
            color="warning"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Would you like to extend your session or log out now?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleLogout} color="inherit" startIcon={<LogoutIcon />}>
          Logout Now
        </Button>
        <Button
          onClick={handleExtendSession}
          variant="contained"
          color="primary"
          autoFocus
          startIcon={<RefreshIcon />}
        >
          Extend Session
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SessionExpirationWarning
